import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

export default function FAQSection() {
  const navigate = useNavigate();

  const goToConsultation = (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();
    sessionStorage.setItem('scrollToSection', 'contact');
    navigate('/');
  };
  const [openIndex, setOpenIndex] = useState<number | null>(0);

  const faqs = [
    {
      category: 'Investment & ROI',
      questions: [
        {
          q: 'What is the total investment required for STEM lab setup?',
          a: 'The investment ranges from ₹8-25 Lakhs depending on the solution package (STEM Lab, AI & Robotics Lab, or combined). This includes complete infrastructure, equipment, software licenses, teacher training, and first-year support. We offer flexible payment terms and customizable packages based on your school\'s specific needs and budget.'
        },
        {
          q: 'What is the expected ROI and timeline?',
          a: 'Most schools see a 30-40% increase in admissions within the first year, with premium fee positioning. The investment typically pays for itself within 2-3 years through increased enrollments, higher fee structure, and enhanced reputation. Additionally, you gain long-term benefits in student outcomes and school branding.'
        },
        {
          q: 'Are there any hidden costs?',
          a: 'No hidden costs. Our pricing is transparent and includes everything: infrastructure setup, equipment, software licenses, teacher training, LMS access, maintenance, and ongoing support. The only additional costs would be optional upgrades or expansion of services beyond the agreed package.'
        }
      ]
    },
    {
      category: 'Implementation',
      questions: [
        {
          q: 'How long does the complete implementation take?',
          a: 'The typical implementation timeline is 12-16 weeks from contract signing to full operational launch. This includes infrastructure setup (4 weeks), teacher training (2 weeks), pilot phase (2 weeks), and full rollout. We work closely with your schedule to minimize disruption to regular academic activities.'
        },
        {
          q: 'What are the space requirements?',
          a: 'A minimum of 800-1200 sq ft dedicated space is required for a standard STEM lab. The space should have adequate ventilation, electrical capacity (15-20 KW), and network connectivity. Our team conducts a detailed site assessment and provides specific recommendations based on your infrastructure.'
        },
        {
          q: 'Will it disrupt our regular academic schedule?',
          a: 'We plan the implementation to minimize disruption. Major construction and setup work can be done during holidays or weekends. Teacher training is scheduled flexibly, and the pilot phase involves only selected groups. Full integration happens gradually over the 12-month partnership cycle.'
        }
      ]
    },
    {
      category: 'Curriculum & Assessment',
      questions: [
        {
          q: 'How does the curriculum align with CBSE/ICSE/State Boards?',
          a: 'Our curriculum is specifically designed to complement and enhance your existing board curriculum. We map STEM activities to specific chapters and learning outcomes in Mathematics, Science, and Computer Science. All programs are NEP 2020 compliant and support competency-based learning frameworks.'
        },
        {
          q: 'How are students assessed?',
          a: 'We use a comprehensive assessment framework including project-based evaluations, practical demonstrations, digital assessments, and portfolio reviews. The LMS provides automated grading for theory components, while teachers assess hands-on projects using detailed rubrics. Parents receive regular progress reports through the portal.'
        }
      ]
    },
    {
      category: 'Support & Training',
      questions: [
        {
          q: 'What training do teachers receive?',
          a: 'Teachers undergo an intensive 40-hour initial training program covering STEM pedagogy, equipment operation, curriculum delivery, and assessment methods. This is followed by monthly professional development workshops, access to a resource library with 1000+ lesson plans, and 24/7 technical support. We also provide certification programs for career advancement.'
        },
        {
          q: 'What ongoing support is provided?',
          a: 'Ongoing support includes: monthly teacher workshops, quarterly curriculum updates, 24/7 technical helpdesk, annual equipment maintenance, software updates, access to peer community of 500+ teachers, and dedicated account manager for your school. We also conduct quarterly performance reviews and provide recommendations for improvement.'
        }
      ]
    },
    {
      category: 'Safety & Compliance',
      questions: [
        {
          q: 'What safety certifications do you provide?',
          a: 'We are ISO 9001:2015 certified and all our equipment meets international safety standards (CE/UL). Each lab setup undergoes a comprehensive safety audit before handover. We provide complete documentation for educational board approvals, fire safety compliance, and insurance requirements. All instructors are trained in safety protocols and emergency procedures.'
        },
        {
          q: 'What insurance coverage is included?',
          a: 'Our package includes comprehensive insurance covering equipment damage, student accidents during lab activities, and liability coverage. We also ensure your school has proper documentation for your existing insurance policies. Detailed safety protocols and emergency procedures are provided to minimize risks.'
        }
      ]
    },
    {
      category: 'Customization',
      questions: [
        {
          q: 'Can the program be customized for our school?',
          a: 'Absolutely! We understand every school is unique. Our team works closely with you to customize the curriculum, lab design, equipment selection, and implementation timeline based on your specific needs, student demographics, existing infrastructure, and budget. We can also incorporate your school branding and specific educational philosophy.'
        },
        {
          q: 'Can we add more programs later?',
          a: 'Yes, our partnership model is designed for scalability. You can start with a basic STEM lab and add AI & Robotics, Innovation Workshops, or Career Guidance programs as you grow. We offer upgrade packages and expansion options at preferential rates for existing partners. Many schools start small and expand based on student response and budget availability.'
        }
      ]
    }
  ];

  return (
    <section className="py-24 bg-white">
      <div className="max-w-4xl mx-auto px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            Frequently Asked Questions
          </h2>
          <p className="text-xl text-gray-600">
            Addressing principal concerns and common queries
          </p>
        </div>

        {/* FAQ Categories */}
        <div className="space-y-8">
          {faqs.map((category, catIdx) => (
            <div key={catIdx}>
              <h3 className="text-2xl font-bold text-gray-900 mb-6 flex items-center gap-3">
                <i className="ri-question-line text-blue-600"></i>
                {category.category}
              </h3>
              <div className="space-y-4">
                {category.questions.map((faq, faqIdx) => {
                  const globalIndex = faqs.slice(0, catIdx).reduce((acc, cat) => acc + cat.questions.length, 0) + faqIdx;
                  const isOpen = openIndex === globalIndex;

                  return (
                    <div
                      key={faqIdx}
                      className="bg-white border-2 border-gray-200 rounded-xl overflow-hidden hover:border-blue-400 transition-all"
                    >
                      <button
                        onClick={() => setOpenIndex(isOpen ? null : globalIndex)}
                        className="w-full px-6 py-5 flex items-center justify-between text-left cursor-pointer hover:bg-gray-50 transition-all"
                      >
                        <span className="text-lg font-semibold text-gray-900 pr-4">{faq.q}</span>
                        <i
                          className={`${
                            isOpen ? 'ri-subtract-line' : 'ri-add-line'
                          } text-2xl text-blue-600 flex-shrink-0 transition-transform`}
                        ></i>
                      </button>
                      {isOpen && (
                        <div className="px-6 pb-6 pt-2">
                          <p className="text-gray-700 leading-relaxed">{faq.a}</p>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>

        {/* CTA */}
        <div className="mt-16 bg-gradient-to-r from-blue-600 to-purple-600 rounded-3xl p-12 text-center text-white">
          <h3 className="text-3xl font-bold mb-4">Didn't Find Your Answer?</h3>
          <p className="text-xl text-blue-100 mb-8">
            Schedule a call with our academic consultant for personalized guidance
          </p>
          <a
            href="/#contact"
            onClick={goToConsultation}
            className="inline-block px-8 py-4 bg-white text-blue-600 text-lg font-semibold rounded-lg hover:bg-gray-100 transition-all shadow-xl whitespace-nowrap cursor-pointer"
          >
            Schedule a Call
          </a>
        </div>
      </div>
    </section>
  );
}
